import {
    fetchEntities,
    getHomeAssistantServiceActions,
} from '../../../services/homeAssistantService.js'

function asObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function normalizeEntity(entity) {
    if (typeof entity === 'string') return { entity_id: entity, state: '', attributes: {} }
    if (!entity || typeof entity !== 'object' || typeof entity.entity_id !== 'string') return null
    return {
        ...entity,
        state: entity.state ?? '',
        attributes: asObject(entity.attributes),
    }
}

function normalizeOptions(options) {
    return options.map(option => {
        const value = option?.value ?? option
        return {
            value: String(value ?? ''),
            label: String(option?.label ?? value ?? ''),
            ...option,
        }
    }).filter(option => option.value)
}

/**
 * Home Assistant metadata boundary for the Designer.
 *
 * Failed refreshes intentionally leave the last successful metadata in place.
 * Consumers can inspect metadataAvailable/lastError, while saved values stay
 * selectable and exportable during a connection outage.
 */
export class HomeAssistantMetadataAdapter {
    constructor(settings = null) {
        this.settings = settings
        this.settingsKey = this.connectionKey(settings)
        this.entities = []
        this.services = []
        this.entitiesAvailable = false
        this.servicesAvailable = false
        this.lastError = ''
        this.lastEntityError = ''
        this.lastServiceError = ''
    }

    setSettings(settings) {
        const nextKey = this.connectionKey(settings)
        if (this.settingsKey !== nextKey) {
            this.entities = []
            this.services = []
            this.entitiesAvailable = false
            this.servicesAvailable = false
            this.lastEntityError = ''
            this.lastServiceError = ''
        }
        this.settings = settings
        this.settingsKey = nextKey
        return this
    }

    connectionKey(settings) {
        const hass = settings?.hass ?? {}
        return `${hass.protocol ?? ''}://${hass.host ?? ''}:${hass.port ?? ''}|${hass.token ?? ''}`
    }

    setEntities(entities) {
        this.entities = (Array.isArray(entities) ? entities : []).map(normalizeEntity).filter(Boolean)
        this.entitiesAvailable = true
        this.lastEntityError = ''
        return this.entities
    }

    setServices(services) {
        this.services = Array.isArray(services) ? services : []
        this.servicesAvailable = true
        this.lastServiceError = ''
        return this.services
    }

    async loadEntities({ refresh = false } = {}) {
        try {
            this.setEntities(await fetchEntities(this.settings, { refresh }))
        } catch (error) {
            this.lastEntityError = String(error)
            this.lastError = this.lastEntityError
        }
        return this.entities
    }

    async loadServices({ refresh = false } = {}) {
        try {
            this.setServices(await getHomeAssistantServiceActions(this.settings, { refresh }))
        } catch (error) {
            this.lastServiceError = String(error)
            this.lastError = this.lastServiceError
        }
        return this.services
    }

    entity(entityId) {
        return this.entities.find(item => item.entity_id === entityId) ?? null
    }

    entityOptions({ domain = '', current = '', includeUnknown = true } = {}) {
        const normalizedDomain = Array.isArray(domain) ? domain : (domain ? [domain] : [])
        const options = this.entities
            .filter(entity => normalizedDomain.length === 0 || normalizedDomain.includes(entity.entity_id.split('.')[0]))
            .map(entity => ({
                value: entity.entity_id,
                label: entity.attributes.friendly_name
                    ? `${entity.attributes.friendly_name} (${entity.entity_id})`
                    : entity.entity_id,
                name: entity.attributes.friendly_name ?? '',
                state: entity.state,
                entity,
            }))
        return this.withUnknown(options, current, includeUnknown, 'Unavailable entity')
    }

    domainOptions({ current = '' } = {}) {
        const domains = [...new Set(this.entities.map(entity => entity.entity_id.split('.')[0]))].sort()
        return this.withUnknown(domains.map(domain => ({ value: domain, label: domain })), current, true, 'Unavailable domain')
    }

    attributeOptions(entityId, { current = '', includeUnknown = true, extra = [] } = {}) {
        const entity = this.entity(entityId)
        const options = [
            ...Object.keys(entity?.attributes ?? {}).map(attribute => ({ value: attribute, label: attribute })),
            ...normalizeOptions(extra),
        ]
        const unique = [...new Map(options.map(option => [option.value, option])).values()]
        return this.withUnknown(unique, current, includeUnknown, 'Saved attribute')
    }

    stateOptions(entityId, { current = '', includeUnknown = true } = {}) {
        const state = this.entity(entityId)?.state
        const options = state ? [{ value: state, label: state }] : []
        return this.withUnknown(options, current, includeUnknown, 'Saved state')
    }

    service(serviceId) {
        return this.services.find(option => option.value === serviceId) ?? null
    }

    serviceOptions({ recommendedDomains = [], current = '', includeAll = true } = {}) {
        const recommended = new Set((recommendedDomains ?? []).filter(Boolean))
        let options = this.services
        if (!includeAll) options = options.filter(option => recommended.has(option.domain))
        const mapped = options.map(option => {
            if (option.recommended !== recommended.has(option.domain)) {
                Object.defineProperty(option, 'recommended', {
                    value: recommended.has(option.domain),
                    enumerable: false,
                    configurable: true,
                })
            }
            if (!option.label) option.label = option.value
            return option
        })
        mapped.sort((left, right) => Number(right.recommended) - Number(left.recommended) || left.value.localeCompare(right.value))
        return this.withUnknown(mapped, current, true, 'Saved service')
    }

    serviceFields(serviceId) {
        return asObject(this.service(serviceId)?.fields)
    }

    serviceTarget(serviceId) {
        return asObject(this.service(serviceId)?.target)
    }

    selectorForEntity({ domain = '', current = '' } = {}) {
        return { kind: 'entity', filters: { domain }, current, preserveUnknown: true }
    }

    selectorForAttribute(entityId, { current = '', extra = [] } = {}) {
        return { kind: 'attribute', entityId, options: extra, current, preserveUnknown: true }
    }

    selectorForState(entityId, { current = '' } = {}) {
        return { kind: 'state', entityId, current, preserveUnknown: true }
    }

    selectorForService({ recommendedDomains = [], current = '' } = {}) {
        return { kind: 'service', recommendedDomains, current, includeAll: true, preserveUnknown: true }
    }

    withUnknown(options, current, includeUnknown, label) {
        if (!includeUnknown || !current || options.some(option => option.value === current)) return options
        return [{ value: current, label: `${current} (${label})`, unknown: true }, ...options]
    }
}

